import { get } from "lodash";
import { array, bool, number, object } from "prop-types";
import React, { useEffect, useState } from "react";
import { BrowserView, MobileView } from "react-device-detect";
import { useSelector } from "react-redux";

import { DfpComponent } from "../../ads/dfp-component";
import RecommendedStories from "../../story-templates/story-elements/RecommendedStories";
import TrendingStories from "../../story-templates/story-elements/TrendingStories";
import Listing500India from "../Listing500India";
import ProfileRanking from "../ProfileRanking";
import { LatestEditionMagazineBlock } from "../LatestEditionMagazineBlock";
import StoryAiHubPromoWidget from "../StoryAiHubPromoWidget";
import "./ad-component.m.css";
import VideoInStory from "../VideoInStory";

const AdSlot = ({ story, networkId, adIndexNo, pathIndex, isAdfreePage = false }) => {
  const [adComponent, setAdComponent] = useState(<></>);

  const desktopId = `story-page-inpage-leaderboard${adIndexNo >= 0 ? "-" + adIndexNo : ""}`;
  const mobileId = `story-page-inpage-mRec-ad${adIndexNo >= 0 ? "-" + adIndexNo : ""}`;

  useEffect(() => {
    setAdComponent(
      <>
        <BrowserView>
          <DfpComponent
            adStyleName="ad-slot-size-728x90-300x250"
            id={desktopId}
            path={
              story["story-template"] === "live-blog"
                ? `/${networkId}/RPSG_Fortune_Desktop/RPSG_FRTN_Desk_LIVBLG_Leaderboard_${pathIndex}`
                : story["story-template"] === "photo"
                ? `/${networkId}/RPSG_Fortune_Desktop/RPSG_FRTN_Desk_PHT_AS_Leaderboard_${pathIndex > 0 ? 2 : 1}`
                : `/${networkId}/RPSG_Fortune_Desktop/RPSG_FRTN_Desk_AS_Leaderboard_${pathIndex}`
            }
            adSlotConfigName="leaderboard_ad"
            showText={true}
          />
        </BrowserView>
        <MobileView>
          <DfpComponent
            adStyleName="ad-slot-size-300x250"
            id={mobileId}
            path={
              story["story-template"] === "live-blog"
                ? `/${networkId}/RPSG_Fortune_Mob/RPSG_FRTN_Mob_LIVBLG_mREC${
                    pathIndex - 1 > 0 ? "_" + (pathIndex - 1) : ""
                  }`
                : story["story-template"] === "photo"
                ? `/${networkId}/RPSG_Fortune_Mob/RPSG_FRTN_Mob_PHT_AS_mREC${pathIndex > 0 ? "_" + pathIndex : ""}`
                : `/${networkId}/RPSG_Fortune_Mob/RPSG_FRTN_Mob_AS_mREC${
                    pathIndex - 1 > 0 ? "_" + (pathIndex - 1) : ""
                  }`
            }
            adSlotConfigName="mRec_ad"
            showText={true}
          />
        </MobileView>
      </>
    );

    // 🧹 Cleanup: destroy slots on unmount
    if (window.googletag && window.googletag.pubads) {
      const slots = window.googletag
        .pubads()
        .getSlots()
        .filter((slot) => {
          const id = slot.getSlotElementId();
          return id === desktopId || id === mobileId;
        });

      if (slots.length > 0) {
        window.googletag.destroySlots(slots);
      }
    }
  }, []); // runs once

  return !isAdfreePage ? adComponent : null;
};

AdSlot.defaultProps = {
  isAdfreePage: false,
};

AdSlot.propTypes = {
  story: object.isRequired,
  networkId: object.isRequired,
  adIndexNo: number.isRequired,
  pathIndex: number.isRequired,
  isAdfreePage: bool,
};

const TaboolaInArticleAd = () => {
  const rawScript = `window._taboola = window._taboola || [];
  _taboola.push({
    mode: 'thumbnails-mid',
    container: 'taboola-mid-article-thumbnails',
    placement: 'Mid Article Thumbnails',
    target_type: 'mix'
  });`;

  return (
    <>
      <div id="taboola-mid-article-thumbnails" styleName="taboola"></div>
      <script type="text/javascript" dangerouslySetInnerHTML={{ __html: rawScript }}></script>
    </>
  );
};

const AdComponent = ({ adIndexNo, isDesktop, story, recommendedStories, moreVideos }) => {
  // Has to be memoized, otherwise it will re-render on every story card render
  const qtState = useSelector((state) => get(state, ["qt"], {}));
  const qtConfig = get(qtState, ["config"], {});
  const isAdfreePage = qtConfig.subscriber === "true";
  const widgetConfig = get(qtState, ["config", "ads-config", "widgets"], {});
  const networkId = get(qtState, ["config", "ads-config", "network_id"], {});
  const taboola = get(qtState, ["config", "publisher-attributes", "taboola"], true);
  const isWebView = get(qtState, ["config", "isWebView"], false) === true;
  const { magazineWidget = "", rankingWidget = "" } = widgetConfig;
  const hasTaboolaAd = taboola && !isAdfreePage;
  const secondAdPathIndex = hasTaboolaAd ? 2 : 3;
  const thirdAdPathIndex = hasTaboolaAd ? 3 : 4;
  const fourthAdPathIndex = hasTaboolaAd ? 4 : 5;

  const getFilteredAddComp = () => {
    return {
      0: (
        <>
          <AdSlot story={story} networkId={networkId} adIndexNo={adIndexNo} pathIndex={1} isAdfreePage={isAdfreePage} />
          <StoryAiHubPromoWidget story={story} qtConfig={qtConfig} />
        </>
      ),
      1: hasTaboolaAd ? (
        <TaboolaInArticleAd />
      ) : (
        <AdSlot story={story} networkId={networkId} adIndexNo={adIndexNo} pathIndex={2} isAdfreePage={isAdfreePage} />
      ),
      2: (
        <>
          {story["story-template"] === "text" ? (
            <VideoInStory moreVideos={qtState?.data?.moreVideos} story={story} config={qtConfig} />
          ) : magazineWidget.show ? (
            <LatestEditionMagazineBlock story={story} qtConfig={qtConfig} />
          ) : (
            <AdSlot
              story={story}
              networkId={networkId}
              adIndexNo={adIndexNo}
              pathIndex={secondAdPathIndex}
              isAdfreePage={isAdfreePage}
            />
          )}
        </>
      ),
      3: (
        <RecommendedStories
          sectionId={story?.sections[0]?.id}
          storyId={story?.id}
          recommendedStories={recommendedStories}
          qtConfig={qtConfig}
        />
      ),
      4: (
        <AdSlot
          story={story}
          networkId={networkId}
          adIndexNo={adIndexNo}
          pathIndex={thirdAdPathIndex}
          isAdfreePage={isAdfreePage}
        />
      ),
      6: (
        <>
          {story["story-template"] === "live-blog" ? (
            <AdSlot
              story={story}
              networkId={networkId}
              adIndexNo={adIndexNo}
              pathIndex={fourthAdPathIndex}
              isAdfreePage={isAdfreePage}
            />
          ) : rankingWidget.show && rankingWidget.entityType && rankingWidget.entityType !== "" ? (
            rankingWidget.entityType === "FortyUnderForty" || rankingWidget.entityType === "MostPowerfulWomen" ? (
              <ProfileRanking
                collection={qtState.data.homeEntities[rankingWidget.entityType]}
                storyPage={true}
                config={qtState?.config}
                story={qtState?.data?.story}
              />
            ) : (
              <Listing500India
                collection={qtState.data.homeEntities[rankingWidget.entityType]}
                storyPage={true}
                story={qtState?.data?.story}
              />
            )
          ) : (
            <AdSlot
              story={story}
              networkId={networkId}
              adIndexNo={adIndexNo}
              pathIndex={fourthAdPathIndex}
              isAdfreePage={isAdfreePage}
            />
          )}
        </>
      ),
      7: <TrendingStories story={story} qtConfig={qtConfig} />,
      default: <></>,
    };
  };

  const getFilteredAddCompForWebView = () => {
    const original = {
      0: <AdSlot story={story} networkId={networkId} adIndexNo={adIndexNo} pathIndex={1} isAdfreePage={isAdfreePage} />,
      1: <AdSlot story={story} networkId={networkId} adIndexNo={adIndexNo} pathIndex={2} isAdfreePage={isAdfreePage} />,
      2: <AdSlot story={story} networkId={networkId} adIndexNo={adIndexNo} pathIndex={3} isAdfreePage={isAdfreePage} />,
      3: <AdSlot story={story} networkId={networkId} adIndexNo={adIndexNo} pathIndex={4} isAdfreePage={isAdfreePage} />,
      4: <AdSlot story={story} networkId={networkId} adIndexNo={adIndexNo} pathIndex={5} isAdfreePage={isAdfreePage} />,
      5: <></>,
      6: <AdSlot story={story} networkId={networkId} adIndexNo={adIndexNo} pathIndex={6} isAdfreePage={isAdfreePage} />,

      7: <AdSlot story={story} networkId={networkId} adIndexNo={adIndexNo} pathIndex={7} isAdfreePage={isAdfreePage} />,
      default: <></>,
    };

    return original;
  };

  let addComp = "";
  if (isWebView) {
    addComp = getFilteredAddCompForWebView();
  } else {
    addComp = getFilteredAddComp();
  }

  // Removing all ads and components/widgets from the body of sponsored articles
  if (story["story-template"] === "sponsored" || story["story-template"] === "creator") {
    return null;
  }

  if (story["story-template"] === "photo") {
    return adIndexNo === 0 ? (
      <>
        <AdSlot story={story} networkId={networkId} adIndexNo={adIndexNo} pathIndex={0} isAdfreePage={isAdfreePage} />
        <StoryAiHubPromoWidget story={story} qtConfig={qtConfig} />
      </>
    ) : adIndexNo === 1 ? (
      <AdSlot story={story} networkId={networkId} adIndexNo={adIndexNo} pathIndex={1} isAdfreePage={isAdfreePage} />
    ) : (
      <AdSlot story={story} networkId={networkId} adIndexNo={adIndexNo} pathIndex={2} isAdfreePage={isAdfreePage} />
    );
  }

  return (
    <>
      {adIndexNo <= 6 ? addComp[`${adIndexNo}`] : null}
      {adIndexNo > 6 && (
        <AdSlot story={story} networkId={networkId} adIndexNo={adIndexNo} pathIndex={5} isAdfreePage={isAdfreePage} />
      )}
    </>
  );
};

AdComponent.propTypes = {
  isDesktop: bool,
  adIndexNo: number,
  story: object,
  recommendedStories: array,
  moreVideos: array,
};

export default AdComponent;
